<template>
  <original-card
    class="Panel"
    :class="{
      'Panel--fullWidth': fullSize || fullWidth,
      'Panel--fullHeight': fullSize,
      ['Panel--' + size]: size
    }"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template
      v-slot:header
      v-if="title || icon || $slots.header || $slots['header-actions']"
    >
      <!-- @slot Entête général -->
      <slot name="header">
        <div class="Panel-header">
          <component
            :is="headerComponent"
            class="Panel-headerTitle"
            v-if="title"
          >
            <ubi-icon class="Panel-headerIcon" v-if="icon" :name="icon" />
            {{ title }}
          </component>
          <div class="Panel-headerActions" v-if="$slots['header-actions']">
            <!-- @slot Actions (boutons) de l'entête -->
            <slot name="header-actions"></slot>
          </div>
        </div>
      </slot>
      <el-row
        :type="browserContext.down('md') ? null : 'flex'"
        justify="space-between"
      >
        <el-col :span="browserContext.down('md') ? 24 : null">
          <slot name="selection"></slot>
        </el-col>
        <el-col :span="browserContext.down('md') ? 24 : null" align="right">
          <slot name="actions"></slot>
        </el-col>
      </el-row>
    </template>
    <div class="Panel-main">
      <slot></slot>
      <el-row
        :gutter="48"
        :type="browserContext.down('md') ? null : 'flex'"
        v-if="$slots['left-column'] || $slots['right-column'] || $slots['left-column-bottom'] || $slots['right-column-bottom']"
      >
        <el-col :md="12" class="Panel-leftcolumn">
          <!-- Colonne gauche - élément supérieur -->
          <div v-if="$slots['left-column']" :class="{'Panel-columnItem': true, 'Panel-columnItem--fixed': fixedHeight}">
            <slot name="left-column" />
          </div>

          <!-- Colonne gauche - élément inférieur -->
          <div v-if="$slots['left-column-bottom']" :class="{'Panel-columnItem': true, 'Panel-columnItem--bottom': true, 'Panel-columnItem--fixed': fixedHeight}">
            <slot name="left-column-bottom" />
          </div>

          <hr v-if="browserContext.down('sm')" class="my-3" />
        </el-col>
        <el-col :md="12" class="Panel-rightcolumn">
          <!-- Colonne droite - élément supérieur -->
          <div v-if="$slots['right-column']" :class="{'Panel-columnItem': true, 'Panel-columnItem--fixed': fixedHeight}">
            <slot name="right-column" />
          </div>

          <!-- Colonne droite - élément inférieur -->
          <div v-if="$slots['right-column-bottom']" :class="{'Panel-columnItem': true, 'Panel-columnItem--bottom': true, 'Panel-columnItem--fixed': fixedHeight}">
            <slot name="right-column-bottom" />
          </div>
        </el-col>
      </el-row>
    </div>
    <div class="Panel-footer" v-if="$slots.footer">
      <!-- @slot Pied-de-page général -->
      <slot name="footer"></slot>
    </div>
  </original-card>
</template>

<script>
import { Card } from 'element-ui'

/**
 * Panneau avec tous types d'informations
 */
export default {
  name: 'UbiPanel',
  inject: ['browserContext'],
  components: {
    OriginalCard: Card
  },
  props: {
    /**
     * Titre de la carte, facultatif
     */
    title: String,
    /**
     * Icône de la carte
     */
    icon: String,
    /**
     * Pas de marge sur les côtés
     */
    fullWidth: Boolean,
    /**
     * Aucune marge horizontale ou verticale
     */
    fullSize: Boolean,
    /**
     * Taille du panel
     * - vide = défaut
     * sm = marges et en-têtes moyennes
     * xs = marges et en-têtes réduites
     */
    size: String,
    /**
     * For SM card component Dashboard
     */
    fixedHeight: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    headerComponent () {
      if (this.size === 'sm') return 'h3'
      if (this.size === 'xs') return 'h4'
      return 'h2'
    }
  }
}
</script>

<style lang="scss">
@import "@/theme/_bootstrap.scss";

.Panel {
  @extend .mb-5;

  border-radius: 1px;
  border-bottom-color: $blue;

  .el-card__header {
    @extend .pb-0;
    border-bottom: 0;
  }

  &-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }
  &-headerTitle {
    @extend .p-0, .m-0;
  }
  &-headerIcon {
    @extend .text-primary, .mr-3;
  }

  &-rightcolumn {
    border-left: 1px solid $gray-200;
  }

  &:not(&--xs):not(&--sm) {
    .Panel-header {
      @extend .pb-3;
      border-bottom: 1px solid $blue;
    }
  }
  &--sm {
    .el-card__header,
    .el-card__body {
      padding-left: map-get($spacers, 4) !important;
      padding-right: map-get($spacers, 4) !important;
      padding-top: map-get($spacers, 4) !important;
    }

    .el-card__body {
      padding-bottom: map-get($spacers, 4) !important;
    }
  }

  &.Panel--sm.Panel--fullWidth {
    .Panel-footer {
      @extend .px-4, .pt-4;
    }
  }

  &--xs {
    .el-card__header,
    .el-card__body {
      padding-left: map-get($spacers, 3) !important;
      padding-right: map-get($spacers, 3) !important;
      padding-top: map-get($spacers, 3) !important;
    }

    .el-card__body {
      padding-bottom: map-get($spacers, 3) !important;
    }
  }

  &.Panel--xs.Panel--fullWidth {
    .Panel-footer {
      @extend .px-3, .pt-3;
    }
  }

  .Panel-columnItem--fixed{
    height: 480px;
  }

  &--fullWidth {
    .Panel-footer {
      @extend .px-5, .pt-5;
    }

    .el-table {
      th:first-child,
      td:first-child {
        .cell {
          @extend .pl-5;
        }
      }
      th:last-child,
      td:last-child {
        .cell {
          @extend .pr-5;
        }
      }
    }
  }

  &-footer {
    @extend .pt-4;
  }

  @include media-breakpoint-down(sm) {
    margin-bottom: map-get($spacers, 3) !important;

    &-header {
      flex-direction: column;
    }
    &-headerTitle {
      align-self: flex-start;
    }
    &-headerActions {
      align-self: flex-end;
      margin-top: map-get($spacers, 2) !important;
    }

    .el-card__header,
    .el-card__body {
      padding-top: map-get($spacers, 3) !important;
      padding-left: map-get($spacers, 3) !important;
      padding-right: map-get($spacers, 3) !important;
    }

    .el-card__body {
      padding-bottom: map-get($spacers, 3) !important;
    }

    &-footer,
    &.Panel--sm.Panel--fullWidth &-footer {
      padding-top: map-get($spacers, 3) !important;
    }
  }

  &--fullWidth {
    .el-card__body {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }

  &--fullHeight {
    .el-card__body {
      padding-bottom: 0 !important;
      padding-top: 0 !important;
    }
  }
}
/* Pour IPAD PRO */
@media screen and (min-width: 1024px) and (max-width: 1366px) {
  .Panel {
    max-width: 88%;
  }
}
</style>

<docs>
### Exemple

```javascript
<ubi-panel title="Utilisateur" icon="user" size="sm">
  <el-button slot="header-actions">Modifier</el-button>
  Contenu du panel
</ubi-panel>
```

</docs>
